import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { getAnnotationIdsFromRange } from '@atlaskit/editor-common/utils/annotation';

import { ACTIONS } from './actions';
import { aiAnnotationWatchmenPluginKey } from './ai-annotation-watchmen-plugin-key';
import type { AIAnnotationWatchmenPluginState } from './ai-annotation-watchmen-plugin-key';

const initialState: AIAnnotationWatchmenPluginState = {
	isWatching: false,
	watchFrom: undefined,
	watchTo: undefined,
	startingAnnotationIds: undefined,
};

type AnnotationChange = { annotationId: string; type: 'ADDED' | 'REMOVED' };

/**
 * This plugin watches for changes in annotations within a single range. This is used by AI in determining whether a range
 * edited by AI has changed the annotations after the AI edit.
 *
 * @param options A collection of callbacks to handle annotation changes.
 * @returns
 */
export const createAIAnnotationWatchmenPlugin = (options: {
	onAnnotationsAdded?: (ids: string[]) => void;
	onAnnotationsChanged?: (changes: AnnotationChange[]) => void;
	onAnnotationsRemoved?: (ids: string[]) => void;
}): SafePlugin => {
	return new SafePlugin<AIAnnotationWatchmenPluginState>({
		key: aiAnnotationWatchmenPluginKey,
		state: {
			init: () => initialState,
			apply: (tr, pluginState, _, ns) => {
				if (
					!options.onAnnotationsAdded &&
					!options.onAnnotationsRemoved &&
					!options.onAnnotationsChanged
				) {
					return pluginState;
				}

				const meta = tr.getMeta(aiAnnotationWatchmenPluginKey);
				const { isWatching, watchFrom, watchTo, startingAnnotationIds } = pluginState;

				switch (meta?.action) {
					case ACTIONS.START_WATCHING_REGION: {
						const { from, to } = meta;

						if (from === undefined || to === undefined) {
							return pluginState;
						}

						const mappedFrom = tr.mapping.map(from, -1);
						const mappedTo = tr.mapping.map(to, 1);
						const ids = getAnnotationIdsFromRange(
							{ from: mappedFrom, to: mappedTo },
							tr.doc,
							ns.schema,
						);

						return {
							isWatching: true,
							watchFrom: mappedFrom,
							watchTo: mappedTo,
							startingAnnotationIds: ids,
						};
					}
					case ACTIONS.END_WATCHING_REGION: {
						if (watchFrom === undefined || watchTo === undefined) {
							return pluginState;
						}

						const ids = getAnnotationIdsFromRange(
							{
								from: tr.mapping.map(watchFrom, -1),
								to: tr.mapping.map(watchTo, 1),
							},
							tr.doc,
							ns.schema,
						);

						const removedIds = startingAnnotationIds?.filter((id) => !ids.includes(id)) ?? [];
						const addedIds = ids.filter((id) => !startingAnnotationIds?.includes(id)) ?? [];

						if (!!options.onAnnotationsRemoved && removedIds?.length) {
							options.onAnnotationsRemoved(removedIds);
						}

						if (!!options.onAnnotationsAdded && addedIds?.length) {
							options.onAnnotationsAdded(addedIds);
						}

						if (!!options.onAnnotationsChanged && (removedIds?.length || addedIds?.length)) {
							const changes: AnnotationChange[] = removedIds
								.map<AnnotationChange>((id) => ({ annotationId: id, type: 'REMOVED' }))
								.concat(
									addedIds.map<AnnotationChange>((id) => ({ annotationId: id, type: 'ADDED' })),
								);

							options.onAnnotationsChanged(changes);
						}

						return {
							isWatching: false,
							watchFrom: undefined,
							watchTo: undefined,
							startingAnnotationIds: undefined,
						};
					}
				}

				return {
					isWatching,
					startingAnnotationIds,
					watchFrom: watchFrom !== undefined ? tr.mapping.map(watchFrom, -1) : undefined,
					watchTo: watchTo !== undefined ? tr.mapping.map(watchTo, 1) : undefined,
				};
			},
		},
	});
};
